import { useRolodexEdit } from 'features/fde/rolodexAPI'
import { useGeckoPermissions } from 'features/permissions'
import { useRolodexFileLS } from 'features/rolodex'
import {
  getPublishBlocker,
  getSignBlocker,
  isSignedDocumentCurrent,
} from 'pages/reporting/shared/approval/approvalRules'
import { useCallback, useMemo, useState } from 'react'
import {
  ReportApprovalWorkflow,
  ReportESignatureRecord,
  ReportESignatureState,
} from 'types'

import { useSignedPdfFetcher } from './useSignedPdfFetcher'
import { parsePdfStamp, pickLatestStamped } from './utils/signedPdfPaths'
import { signReport, SignRequest, toPdfBlob } from './utils/signReport'
import { prepareClientCopy } from './utils/stampSignature'

interface UseReportESignaturesParams {
  metadataEntryId: string
  workflow: ReportApprovalWorkflow | undefined
  eSignatures: ReportESignatureState | undefined
  onChanged: (patch: { eSignatures: ReportESignatureState }) => void
  draftRequirement?: { path: string | undefined; blocker: string | undefined }
}

export interface UseReportESignaturesResult {
  records: readonly ReportESignatureRecord[]
  hasDraft: boolean
  isSignedDocumentCurrent: boolean
  publishBlocker: string | undefined
  isBusy: boolean
  sign: (request: SignRequest) => Promise<ReportESignatureRecord>
  voidSignatures: () => Promise<void>
  /** Client-facing copy of the latest signed PDF: cover signatures only, no certificate page. */
  fetchPublishableBlob: () => Promise<Blob | undefined>
}

const EMPTY_RECORDS: readonly ReportESignatureRecord[] = []

/**
 * Sequential signing over one PDF: each signer stamps the previous signer's
 * output (or the latest draft) and uploads a new file. Signature images never
 * leave the PDF; the entry only stores audit records and hashes.
 */
export function useReportESignatures({
  metadataEntryId,
  workflow,
  eSignatures,
  onChanged,
  draftRequirement,
}: UseReportESignaturesParams): UseReportESignaturesResult {
  const { rolodexUploadFile, rolodexDeleteFile, rolodexPatch } =
    useRolodexEdit()
  const { fetchFileBytes, fetchVerifiedSignedPdf } = useSignedPdfFetcher()
  const { user } = useGeckoPermissions()
  const [isBusy, setIsBusy] = useState(false)

  const records = eSignatures?.records ?? EMPTY_RECORDS
  const lastRecord = records.at(-1)

  const { fileRefs: draftRefs } = useRolodexFileLS({
    entry_id: metadataEntryId,
    path: 'reportPDFDrafts/**',
  })
  const latestDraft = useMemo(() => pickLatestStamped(draftRefs), [draftRefs])
  const latestSignedStamp =
    lastRecord && parsePdfStamp(lastRecord.signedDocumentPath)
  const draftBlocker =
    draftRequirement?.blocker ??
    (draftRequirement && latestDraft?.ref.path !== draftRequirement.path
      ? 'Regenerate the draft PDF to include the latest addendum changes before signing or publishing'
      : undefined)

  const persist = useCallback(
    async (next: ReportESignatureState) => {
      await rolodexPatch([
        { id: metadataEntryId, data: { e_signatures: next } },
      ])
      onChanged({ eSignatures: next })
    },
    [metadataEntryId, onChanged, rolodexPatch]
  )

  const sign = useCallback(
    async (request: SignRequest) => {
      if (draftBlocker) throw new Error(draftBlocker)
      if (!user) throw new Error('Your account has not finished loading')
      const blocker = getSignBlocker(workflow, records, request.role, {
        userId: user.id,
        email: user.email,
      })
      if (blocker) throw new Error(blocker)
      const sourcePath = lastRecord?.signedDocumentPath ?? latestDraft?.ref.path
      if (!sourcePath) throw new Error('Generate a draft PDF before signing')

      setIsBusy(true)
      try {
        const record = await signReport({
          request,
          signer: { userId: user.id, name: user.name, email: user.email },
          metadataEntryId,
          sourcePath,
          previousRecord: lastRecord,
          slotIndex: records.length,
          fetchFileBytes: (path) => fetchFileBytes(metadataEntryId, path),
          uploadFile: (blob, path) =>
            rolodexUploadFile(metadataEntryId, blob, path),
        })
        await persist({ records: [...records, record] })
        return record
      } finally {
        setIsBusy(false)
      }
    },
    [
      fetchFileBytes,
      lastRecord,
      latestDraft,
      metadataEntryId,
      persist,
      records,
      rolodexUploadFile,
      user,
      workflow,
      draftBlocker,
    ]
  )

  const voidSignatures = useCallback(async () => {
    if (records.length === 0) return
    setIsBusy(true)
    try {
      await Promise.all(
        records.map((record) =>
          rolodexDeleteFile(metadataEntryId, record.signedDocumentPath)
        )
      )
      await persist({ records: [] })
    } finally {
      setIsBusy(false)
    }
  }, [metadataEntryId, persist, records, rolodexDeleteFile])

  const fetchPublishableBlob = useCallback(async () => {
    if (draftBlocker) throw new Error(draftBlocker)
    if (!lastRecord) return undefined
    const bytes = await fetchVerifiedSignedPdf(metadataEntryId, lastRecord)
    return toPdfBlob(await prepareClientCopy(bytes))
  }, [draftBlocker, fetchVerifiedSignedPdf, lastRecord, metadataEntryId])

  return useMemo(
    () => ({
      records,
      hasDraft: latestDraft !== undefined && !draftBlocker,
      isSignedDocumentCurrent:
        !draftBlocker &&
        isSignedDocumentCurrent(latestDraft?.stamp, latestSignedStamp),
      publishBlocker:
        draftBlocker ??
        getPublishBlocker(workflow, records, {
          latestDraft: latestDraft?.stamp,
          latestSigned: latestSignedStamp,
        }),
      isBusy,
      sign,
      voidSignatures,
      fetchPublishableBlob,
    }),
    [
      fetchPublishableBlob,
      isBusy,
      latestDraft,
      latestSignedStamp,
      records,
      sign,
      voidSignatures,
      workflow,
      draftBlocker,
    ]
  )
}
